"use client";

import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/cn";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  /** Max width on desktop. */
  size?: "sm" | "md" | "lg";
  /** Hide the default close (x) button. */
  hideClose?: boolean;
  className?: string;
  /**
   * Extra classes for the fullscreen wrapper — e.g. a higher z-index when the
   * dialog must layer over another overlay (the lightbox sits at z-[95]).
   */
  overlayClassName?: string;
}

const sizeMap = {
  sm: "sm:max-w-sm",
  md: "sm:max-w-md",
  lg: "sm:max-w-lg",
} as const;

/**
 * Stack of currently open modals. Every open Modal registers a document-level
 * Escape listener, and stopPropagation() does not stop OTHER listeners on the
 * same node — so with a nested dialog (e.g. a ConfirmDialog over an editor
 * modal) one Escape press used to close both layers at once. Each modal
 * pushes an identity token here on open and removes it on close/unmount
 * (spliced wherever it sits, so any close order is safe); the Escape handler
 * only acts — and only then consumes the event for layers below, like the
 * photo lightbox's window keydown — when its own token is topmost.
 *
 * Known edge (non-issue for current consumers): if a parent modal and a
 * nested one mount already-open in the same commit, the child's effect runs
 * first, so the parent would land on top of the stack; in practice nested
 * dialogs only open after user interaction inside an already-open modal.
 */
const openModalStack: object[] = [];

/**
 * Dialog that reads as a bottom sheet on mobile (slides up, rounded top,
 * grab handle, safe-area padding) and a centered card on desktop. Blurred
 * backdrop, Escape to close, body scroll locked while open.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
  hideClose = false,
  className,
  overlayClassName,
}: ModalProps) {
  const t = useTranslations("common");
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  /** Identity token on {@link openModalStack} while this modal is open. */
  const stackEntry = useRef({});

  // Register on the open-modal stack. Keyed on `open` alone (not `onClose`)
  // so parent re-renders can't re-push an outer modal above an inner one.
  useEffect(() => {
    if (!open) return;
    const entry = stackEntry.current;
    openModalStack.push(entry);
    return () => {
      const i = openModalStack.indexOf(entry);
      if (i !== -1) openModalStack.splice(i, 1);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // Only the topmost open modal reacts: a nested ConfirmDialog's Escape
      // must not also close the editor modal underneath it.
      if (openModalStack[openModalStack.length - 1] !== stackEntry.current) return;
      // Consume the event so listeners beneath this layer (e.g. the photo
      // lightbox's window keydown) don't also close on the same press.
      e.stopPropagation();
      onClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  // Focus the panel on open and hand focus back to the opener on close
  // (PhotoLightbox idiom); Tab wraps within the panel while open.
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement;
    panelRef.current?.focus();
    const onTab = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      // Only the topmost open modal traps focus: with a nested ConfirmDialog
      // open, the outer modal would otherwise treat the portaled dialog as
      // "outside" and yank every Tab back into its own panel.
      if (openModalStack[openModalStack.length - 1] !== stackEntry.current) return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      const inside = active instanceof HTMLElement && panel.contains(active);
      if (e.shiftKey) {
        if (!inside || active === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (!inside || active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onTab);
    return () => {
      document.removeEventListener("keydown", onTab);
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
    };
  }, [open]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      className={cn(
        "fixed inset-0 z-50 flex items-end justify-center sm:items-center",
        overlayClassName,
      )}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
    >
      <button
        aria-label={t("close")}
        onClick={onClose}
        className="absolute inset-0 animate-[rise_.2s_ease] bg-black/70 backdrop-blur-sm"
      />

      <div
        ref={panelRef}
        tabIndex={-1}
        className={cn(
          "glass-panel relative flex max-h-[min(92dvh,var(--app-h,100dvh))] w-full flex-col outline-none",
          "animate-scale-in rounded-t-3xl border-white/10 bg-elevated/90 shadow-lift",
          "pb-[max(1.25rem,env(safe-area-inset-bottom))]",
          "sm:mx-4 sm:rounded-3xl sm:pb-6",
          sizeMap[size],
          className,
        )}
      >
        {/* mobile grab handle */}
        <div className="mx-auto mt-3 h-1.5 w-11 shrink-0 rounded-full bg-white/15 sm:hidden" />

        {(title || !hideClose) && (
          <div className="flex items-start gap-4 px-6 pt-5">
            <div className="min-w-0 flex-1">
              {title && (
                <h2
                  id={titleId}
                  className="font-display text-xl font-semibold text-white"
                >
                  {title}
                </h2>
              )}
              {description && (
                <p className="mt-1 text-sm text-white/55">{description}</p>
              )}
            </div>
            {!hideClose && (
              <button
                onClick={onClose}
                aria-label={t("close")}
                className="relative -mr-1.5 -mt-1 flex size-9 shrink-0 items-center justify-center rounded-full text-white/55 transition-colors after:absolute after:-inset-1 hover:bg-white/8 hover:text-white"
              >
                <svg
                  viewBox="0 0 24 24"
                  className="size-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                >
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            )}
          </div>
        )}

        <div className="overflow-y-auto px-6 py-5">{children}</div>

        {footer && (
          <div className="flex flex-col-reverse gap-3 px-6 pt-1 sm:flex-row sm:justify-end">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
